import { Invoice } from '@/data/mockData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency } from '@/lib/utils';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Package,
  DollarSign,
  Edit,
  Download,
  Printer,
  Mail,
  Calendar,
  Building2,
  MessageCircle,
  X,
} from 'lucide-react';

// Define valid variants for Badge component
type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

interface InvoiceDetailsProps {
  invoice: Invoice;
  allInvoices?: Invoice[];
  onEdit: () => void;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

export const InvoiceDetails = ({
  invoice,
  allInvoices = [],
  onEdit,
  onClose,
  onApprove,
  onReject,
}: InvoiceDetailsProps) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Approved':
      case 'Fully Received':
      case 'Verified':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'Rejected':
      case 'Fully Rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'Pending':
      case 'Partially Received':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'Draft':
      case 'Created':
        return <FileText className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPaymentIcon = (status: string) => {
    switch (status) {
      case 'Paid':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'Overdue':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'Pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'Partial':
        return <DollarSign className="h-5 w-5 text-blue-500" />;
      case 'Not Paid':
        return <XCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusVariant = (status: string): BadgeVariant => {
    switch (status) {
      case 'Approved':
      case 'Fully Received':
      case 'Verified':
        return 'default'; // Green/positive status
      case 'Rejected':
      case 'Fully Rejected':
        return 'destructive'; // Red/negative status
      case 'Pending':
      case 'Partially Received':
        return 'secondary'; // Yellow/warning status
      case 'Draft':
      case 'Created':
        return 'outline'; // Grey/neutral status
      default:
        return 'outline';
    }
  };

  const getPaymentVariant = (status: string): BadgeVariant => {
    switch (status) {
      case 'Paid':
        return 'default'; // Green/success
      case 'Pending':
        return 'secondary'; // Yellow/warning
      case 'Overdue':
        return 'destructive'; // Red/danger
      case 'Partial':
        return 'outline'; // Blue/info
      case 'Not Paid':
        return 'outline'; // Grey/neutral
      default:
        return 'outline';
    }
  };

  const isOverdue = () => {
    if (invoice.paymentStatus === 'Paid') return false;
    const dueDate = new Date(invoice.dueDate);
    const today = new Date();
    return dueDate < today;
  };


  const getDaysUntilDue = () => {
    const dueDate = new Date(invoice.dueDate);
    const today = new Date();
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // CTA Button Handlers
  const handleDownloadInvoice = () => {
    // Generate PDF content
    const invoiceContent = `
Invoice: ${invoice.invoiceNumber}
Date: ${new Date(invoice.invoiceDate).toLocaleDateString()}
Due Date: ${new Date(invoice.dueDate).toLocaleDateString()}
Vendor: ${invoice.vendorName}
Total Amount: ${formatCurrency(invoice.totalAmount)}
Status: ${invoice.paymentStatus}
    `.trim();

    // Create and download file
    const blob = new Blob([invoiceContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoice.invoiceNumber}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const handleEmailInvoice = () => {
    const subject = `Invoice ${invoice.invoiceNumber}`;
    const body = `Please find attached invoice ${invoice.invoiceNumber} for ${formatCurrency(invoice.totalAmount)}.`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
  };

  const handleWhatsAppShare = () => {
    const message = `Invoice ${invoice.invoiceNumber} - ${formatCurrency(invoice.totalAmount)} - ${invoice.paymentStatus}`;
    const whatsappLink = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappLink, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{invoice.invoiceNumber}</h2>
            <p className="text-muted-foreground">Invoice Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleDownloadInvoice()}
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handlePrintInvoice()}
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleEmailInvoice()}
          >
            <Mail className="h-4 w-4 mr-2" />
            Email
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleWhatsAppShare()}
            className="bg-green-600 hover:bg-green-700 text-white border-green-600"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            WhatsApp
          </Button>
          <Button size="sm" onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      {/* Status and Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {getStatusIcon(invoice.status)}
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={getStatusVariant(invoice.status)} className="text-sm">
                  {invoice.status}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {getPaymentIcon(invoice.paymentStatus)}
              <div>
                <p className="text-sm text-muted-foreground">Payment Status</p>
                <Badge variant={getPaymentVariant(invoice.paymentStatus)} className="text-sm">
                  {invoice.paymentStatus}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-lg font-semibold">{formatCurrency(invoice.totalAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons for Pending Invoices */}
      {invoice.status === 'Pending' && onApprove && onReject && (
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="font-medium">Invoice Pending Approval</p>
                  <p className="text-sm text-muted-foreground">
                    This invoice requires your approval before processing
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onReject}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button size="sm" onClick={onApprove}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overdue Warning */}
      {isOverdue() && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-950">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">Invoice Overdue</p>
                <p className="text-sm text-red-600 dark:text-red-300">
                  This invoice is past its due date and requires immediate attention
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="all-invoices">All Invoices</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Invoice Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice Number</span>
                  <span className="font-medium">{invoice.invoiceNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">PO Number</span>
                  <span className="font-medium">{invoice.poNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice Date</span>
                  <span className="font-medium">
                    {new Date(invoice.invoiceDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Due Date</span>
                  <span className="font-medium">
                    {new Date(invoice.dueDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Terms</span>
                  <span className="font-medium">{invoice.paymentTerms}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Days Until Due</span>
                  <span
                    className={`font-medium ${
                      getDaysUntilDue() < 0
                        ? 'text-red-600'
                        : getDaysUntilDue() < 7
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}
                  >
                    {getDaysUntilDue() < 0 ? 'Overdue' : `${getDaysUntilDue()} days`}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Vendor Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vendor Name</span>
                  <span className="font-medium">{invoice.vendorName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vendor ID</span>
                  <span className="font-medium">{invoice.vendorId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={getStatusVariant(invoice.status)}>{invoice.status}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Status</span>
                  <Badge variant={getPaymentVariant(invoice.paymentStatus)}>
                    {invoice.paymentStatus}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span className="font-medium">
                    {new Date(invoice.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span className="font-medium">
                    {new Date(invoice.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Financial Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{formatCurrency(invoice.subtotal)}</p>
                  <p className="text-sm text-muted-foreground">Subtotal</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">{formatCurrency(invoice.taxAmount)}</p>
                  <p className="text-sm text-muted-foreground">Tax Amount</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(invoice.totalAmount)}</p>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {invoice.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm bg-muted/50 p-3 rounded">{invoice.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Invoice Items</CardTitle>
              <CardDescription>{invoice.items.length} items in this invoice</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {invoice.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Package className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{item.skuName}</h4>
                        <p className="text-sm text-muted-foreground">{item.skuCode}</p>
                        {item.description && (
                          <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-lg font-bold">{formatCurrency(item.totalPrice)}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.quantity} × {formatCurrency(item.unitPrice)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Payment History</CardTitle>
              <CardDescription>Track payments and payment status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold text-primary">{formatCurrency(invoice.totalAmount)}</p>
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {invoice.paymentStatus === 'Paid'
                        ? formatCurrency(invoice.totalAmount)
                        : invoice.paymentStatus === 'Partial'
                        ? formatCurrency(invoice.totalAmount * 0.5)
                        : formatCurrency(0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Amount Paid</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                      {invoice.paymentStatus === 'Paid'
                        ? formatCurrency(0)
                        : invoice.paymentStatus === 'Partial'
                        ? formatCurrency(invoice.totalAmount * 0.5)
                        : formatCurrency(invoice.totalAmount)}
                    </p>
                    <p className="text-sm text-muted-foreground">Amount Due</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <DollarSign className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Invoice Created</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(invoice.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(invoice.totalAmount)}</p>
                      <Badge variant="outline">Pending</Badge>
                    </div>
                  </div>

                  {invoice.paymentStatus === 'Paid' && (
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50 dark:bg-green-950">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">Payment Received</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(invoice.updatedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(invoice.totalAmount)}</p>
                        <Badge variant="default">Paid</Badge>
                      </div>
                    </div>
                  )}

                  {invoice.paymentStatus === 'Partial' && (
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-yellow-50 dark:bg-yellow-950">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                          <DollarSign className="h-4 w-4 text-yellow-600" />
                        </div>
                        <div>
                          <p className="font-medium">Partial Payment</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(invoice.updatedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(invoice.totalAmount * 0.5)}</p>
                        <Badge variant="outline">Partial</Badge>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Invoices Tab */}
        <TabsContent value="all-invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                All Invoices for {invoice.vendorName}
              </CardTitle>
              <CardDescription>
                Complete invoice history sorted by date (newest first)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {allInvoices.length > 0 ? (
                <div className="space-y-3">
                  {allInvoices
                    .filter(inv => inv.vendorName === invoice.vendorName)
                    .sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime())
                    .map((inv) => (
                      <div 
                        key={inv.id} 
                        className={`flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors ${
                          inv.id === invoice.id ? 'bg-primary/5 border-primary' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                              <FileText className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm">{inv.invoiceNumber}</h4>
                              <div className="flex items-center gap-2">
                                <Badge variant={getStatusVariant(inv.status)} className="text-xs">
                                  {inv.status}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {inv.paymentStatus}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-muted-foreground">
                            <div>
                              <p>PO Number</p>
                              <p className="font-medium text-foreground">{inv.poNumber}</p>
                            </div>
                            <div>
                              <p>Invoice Date</p>
                              <p className="font-medium text-foreground">
                                {new Date(inv.invoiceDate).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p>Due Date</p>
                              <p className="font-medium text-foreground">
                                {new Date(inv.dueDate).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p>Amount</p>
                              <p className="font-medium text-foreground">
                                {formatCurrency(inv.totalAmount)}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          <div className="text-right">
                            <p className="text-sm font-medium">
                              {formatCurrency(inv.totalAmount)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {inv.items?.length || 0} items
                            </p>
                          </div>
                          {inv.id === invoice.id && (
                            <Badge variant="default" className="text-xs">
                              Current
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No other invoices found</p>
                  <p className="text-sm">This is the only invoice for this vendor</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};